import { useAnimatedPercentages } from "@/components/domain/voting/hooks/useAnimatedPercentages";
import { VoteHeader } from "@/components/domain/voting/shared/VoteHeader";
import { VoteOptions } from "@/components/domain/voting/shared/VoteOptions";
import { VoteResults } from "@/components/domain/voting/shared/VoteResults";
import { useVoteData } from "@/hooks/useVoteData";
import { cn } from "@/lib/utils";
import { ExtensionLink } from "components/core/ExtensionLink";

interface VotingCardProps {
    className?: string;
}

export const VotingCard = ({ className }: VotingCardProps) => {
    const { vote, voteState, percentages, submitVote, reset, isLoading } =
        useVoteData();

    const animatedPercentages = useAnimatedPercentages({
        hasVoted: voteState.hasVoted,
        selectedOption: voteState.selectedOption,
        targetPercentages: percentages
    });

    const handleVote = (optionId: number) => {
        submitVote(optionId);
    };

    const hasVoted = voteState.hasVoted;

    if (isLoading) {
        return (
            <div
                className={cn(
                    "h-[400px] w-full animate-pulse rounded-2xl bg-grey-200",
                    className
                )}
            />
        );
    }

    if (!hasVoted) {
        return (
            <div
                className={cn(
                    "flex flex-col rounded-2xl border border-gray-200 bg-white px-4 shadow-sm transition-all duration-200 dark:border-gray-700 dark:bg-black dark:shadow-black/30",
                    className
                )}
            >
                <VoteHeader
                    participantCount={vote.participantCount}
                    hasVoted={false}
                />

                <h3 className="mb-4 font-bold leading-snug text-gray-900 dark:text-white">
                    {vote.question}
                </h3>

                <VoteOptions options={vote.options} onVote={handleVote} />

                <div className="my-2 flex items-center justify-center text-[12px] text-gray-500">
                    총 {vote.participantCount}명 참여
                </div>

                <ExtensionLink
                    href="/vote"
                    className="mb-[16px] block rounded-xl bg-black px-3 py-3 text-center text-sm font-bold text-white transition-all duration-200 hover:bg-gray-600 dark:bg-white dark:text-black dark:hover:bg-gray-300"
                >
                    자세히 보기
                </ExtensionLink>
            </div>
        );
    }

    return (
        <div
            className={cn(
                "flex flex-col rounded-2xl border border-gray-200 bg-white px-4 shadow-sm transition-all duration-200 dark:border-gray-700 dark:bg-black dark:shadow-black/30",
                className
            )}
        >
            <VoteHeader
                participantCount={vote.participantCount}
                hasVoted={true}
            />

            <h3 className="mb-3 font-bold leading-snug text-gray-900 dark:text-white">
                {vote.question}
            </h3>

            <div>
                <VoteResults
                    options={vote.options}
                    percentages={animatedPercentages}
                    selectedOption={voteState.selectedOption}
                />
            </div>

            <div className="my-2 flex items-center justify-center text-[12px] text-gray-500">
                총 {vote.participantCount}명 참여
            </div>

            <div className="mb-2 flex items-center gap-3">
                <button
                    onClick={reset}
                    className="flex-1 border-none bg-transparent p-0 text-sm text-black transition-colors duration-200 hover:text-black hover:underline hover:underline-offset-2 dark:text-white dark:hover:text-white"
                >
                    다시 선택하기
                </button>
                <ExtensionLink
                    href="/vote"
                    className="mb-[16px] block flex-1 rounded-xl bg-black px-3 py-3 text-center text-sm font-bold text-white transition-all duration-200 hover:bg-gray-600 dark:bg-white dark:text-black dark:hover:bg-gray-300"
                >
                    결과 자세히 보기
                </ExtensionLink>
            </div>
        </div>
    );
};
